/*
 * /details/$patientId (page)
 */
import {
	Badge,
	Button,
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	Checkbox,
	Container,
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Input,
	Label,
	Page,
	Placeholder,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
	Textarea,
	toast,
} from "@emr/react-ui"
import { createFileRoute, Link } from "@tanstack/react-router"
import { useState, useRef } from "react"
import { ArrowDownLeft, ArrowUpRight, CheckCircle, Clock, FileDown, FileText, Loader2, Pencil, Plus, Stethoscope, Trash2, UserCheck, X } from "lucide-react"
import { PatientForm } from "../../features/patient/patient-form"
import { AddressForm } from "../../features/address/address-form"
import { PatientHeader } from "../../features/patient/patient-header"
import { trpc } from "../../trpc"
import type { Address, PatientDiagnosis, PatientReferral, Referral } from "@emr/db/client"
import { DIAGNOSIS_TYPES, REFERRAL_DIRECTIONS } from "@emr/db/client"

export const Route = createFileRoute("/details/$patientId")({
	component: DetailsPage,
})

const MOCK_PATIENT = {
	firstName: "...",
	lastName: "",
	dateOfBirth: null,
	bloodType: null,
	allergies: null,
	emergencyContactName: null,
	emergencyContactPhone: null,
}

const ADDRESS_TYPE_LABELS: Record<string, string> = {
	home: "Home",
	work: "Work",
	billing: "Billing",
	shipping: "Shipping",
	other: "Other",
}

function DetailsPage() {
	const { patientId } = Route.useParams()
	const utils = trpc.useUtils()

	const [editingAddressId, setEditingAddressId] = useState<string | null>(null)
	const [showAddAddress, setShowAddAddress] = useState(false)

	// Diagnosis dialog state
	const [diagnosisDialogOpen, setDiagnosisDialogOpen] = useState(false)
	const [editingDiagnosis, setEditingDiagnosis] = useState<PatientDiagnosis | null>(null)
	const [dxDiagnosisId, setDxDiagnosisId] = useState("")
	const [dxType, setDxType] = useState<"primary" | "secondary">("secondary")
	const [dxYear, setDxYear] = useState("")
	// Primary conflict confirmation
	const [primaryConflictOpen, setPrimaryConflictOpen] = useState(false)
	const [primaryToDowngrade, setPrimaryToDowngrade] = useState<PatientDiagnosis | null>(null)

	// Referral dialog state
	const [referralDialogOpen, setReferralDialogOpen] = useState(false)
	const [editingReferral, setEditingReferral] = useState<PatientReferral | null>(null)
	const [refReferralId, setRefReferralId] = useState("")

	// Referral PDF dialog state
	const [referralPdfDialogOpen, setReferralPdfDialogOpen] = useState(false)
	const [referralPdfTarget, setReferralPdfTarget] = useState<PatientReferral | null>(null)
	const [referralPdfReason, setReferralPdfReason] = useState("")
	const [referralPdfPractitionerId, setReferralPdfPractitionerId] = useState("")
	const [referralPdfPriority, setReferralPdfPriority] = useState("")
	const [referralPdfPriorityOther, setReferralPdfPriorityOther] = useState("")
	const [referralPdfTypes, setReferralPdfTypes] = useState<string[]>([])
	const [referralPdfTypeOther, setReferralPdfTypeOther] = useState("")
	const [refDirection, setRefDirection] = useState<"incoming" | "outgoing">("outgoing")
	const [refDate, setRefDate] = useState("")
	const [refNotes, setRefNotes] = useState("")

	// Assign form dialog state
	const [assignFormDialogOpen, setAssignFormDialogOpen] = useState(false)
	const [assignFormId, setAssignFormId] = useState("")

	// PCP dialog state
	const [pcpDialogOpen, setPcpDialogOpen] = useState(false)
	const [pcpMode, setPcpMode] = useState<"search" | "create">("search")
	const [pcpSearchTerm, setPcpSearchTerm] = useState("")
	const [pcpDebouncedTerm, setPcpDebouncedTerm] = useState("")
	const [pcpSelected, setPcpSelected] = useState<Referral | null>(null)
	const pcpDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const [pcpNewForm, setPcpNewForm] = useState({
		companyName: "", contactName: "", address: "", phoneNumber: "", faxNumber: "", email: "", isPcp: true,
	})

	const { data: patient, isLoading: patientLoading } =
		trpc.patient.getByFilter.useQuery({ where: { id: patientId } })

	const { data: addresses, isLoading: addressesLoading } =
		trpc.address.getAllByFilter.useQuery({ where: { patientId, active: true } })

	const { data: patientDiagnoses } =
		trpc.patientDiagnosis.getAllByFilter.useQuery({ where: { patientId, active: true } })

	const { data: allDiagnoses } =
		trpc.diagnosis.getAllByFilter.useQuery({ where: { active: true } })

	const { data: patientReferrals } =
		trpc.patientReferral.getAllByFilter.useQuery({ where: { patientId, active: true } })

	const { data: allReferrals } =
		trpc.referral.getAllByFilter.useQuery({ where: { active: true } })

	const { data: pcpSearchResults, isLoading: pcpSearchLoading } =
		trpc.referral.search.useQuery(
			{ term: pcpDebouncedTerm },
			{ enabled: pcpDebouncedTerm.length >= 2 },
		)

	const { data: currentPcp } =
		trpc.referral.getByFilter.useQuery(
			{ where: { id: patient?.pcpId ?? "" } },
			{ enabled: !!patient?.pcpId },
		)

	const { data: allPractitioners } =
		trpc.practitioner.getAllByFilter.useQuery({ where: { active: true } })

	const { data: availableForms } =
		trpc.form.getAllByFilter.useQuery({ where: { active: true } })

	const { data: patientForms, refetch: refetchPatientForms } =
		trpc.patientForm.getAllWithForms.useQuery({ where: { patientId, active: true } })

	const assignForm = trpc.patientForm.create.useMutation({
		onSuccess: () => {
			refetchPatientForms()
			toast.success("Form assigned")
			setAssignFormDialogOpen(false)
			setAssignFormId("")
		},
		onError: (e) => toast.error(e.message),
	})

	const deleteAddress = trpc.address.update.useMutation({
		onSuccess: () => {
			utils.address.getAllByFilter.invalidate()
			toast.success("Address removed")
		},
	})

	const createDiagnosis = trpc.patientDiagnosis.create.useMutation({
		onSuccess: () => {
			utils.patientDiagnosis.getAllByFilter.invalidate()
			toast.success("Diagnosis added")
			closeDiagnosisDialog()
		},
		onError: (e) => toast.error(e.message),
	})
	const updateDiagnosis = trpc.patientDiagnosis.update.useMutation({
		onSuccess: () => {
			utils.patientDiagnosis.getAllByFilter.invalidate()
			toast.success("Diagnosis updated")
			closeDiagnosisDialog()
		},
		onError: (e) => toast.error(e.message),
	})
	const removeDiagnosis = trpc.patientDiagnosis.update.useMutation({
		onSuccess: () => {
			utils.patientDiagnosis.getAllByFilter.invalidate()
			toast.success("Diagnosis removed")
		},
		onError: (e) => toast.error(e.message),
	})
	const downgradePrimary = trpc.patientDiagnosis.update.useMutation({
		onSuccess: () => utils.patientDiagnosis.getAllByFilter.invalidate(),
		onError: (e) => toast.error(e.message),
	})

	const createReferral = trpc.patientReferral.create.useMutation({
		onSuccess: () => {
			utils.patientReferral.getAllByFilter.invalidate()
			toast.success("Referral added")
			closeReferralDialog()
		},
		onError: (e) => toast.error(e.message),
	})
	const updateReferral = trpc.patientReferral.update.useMutation({
		onSuccess: () => {
			utils.patientReferral.getAllByFilter.invalidate()
			toast.success("Referral updated")
			closeReferralDialog()
		},
		onError: (e) => toast.error(e.message),
	})
	const removeReferral = trpc.patientReferral.update.useMutation({
		onSuccess: () => {
			utils.patientReferral.getAllByFilter.invalidate()
			toast.success("Referral removed")
		},
		onError: (e) => toast.error(e.message),
	})

	function resetReferralPdfDialog() {
		setReferralPdfDialogOpen(false)
		setReferralPdfTarget(null)
		setReferralPdfReason("")
		setReferralPdfPractitionerId("")
		setReferralPdfPriority("")
		setReferralPdfPriorityOther("")
		setReferralPdfTypes([])
		setReferralPdfTypeOther("")
	}

	const generateReferralPdf = trpc.patientReferral.generatePdf.useMutation({
		onSuccess: (result) => {
			window.open(result.downloadUrl, "_blank")
			toast.success("Referral PDF generated")
			resetReferralPdfDialog()
		},
		onError: (e) => toast.error(e.message),
	})

	const setPcp = trpc.patient.update.useMutation({
		onSuccess: () => {
			utils.patient.getByFilter.invalidate()
			utils.referral.getByFilter.invalidate()
			toast.success("Primary Care Provider updated")
			closePcpDialog()
		},
		onError: (e) => toast.error(e.message),
	})

	const createPcpReferral = trpc.referral.create.useMutation({
		onSuccess: (created) => {
			setPcp.mutate({ id: patientId, pcpId: created.id } as any)
		},
		onError: (e) => toast.error(e.message),
	})

	const removePcp = trpc.patient.update.useMutation({
		onSuccess: () => {
			utils.patient.getByFilter.invalidate()
			utils.referral.getByFilter.invalidate()
			toast.success("Primary Care Provider removed")
		},
		onError: (e) => toast.error(e.message),
	})

	function openPcpDialog() {
		setPcpMode("search")
		setPcpSearchTerm("")
		setPcpDebouncedTerm("")
		setPcpSelected(null)
		setPcpNewForm({ companyName: "", contactName: "", address: "", phoneNumber: "", faxNumber: "", email: "", isPcp: true })
		setPcpDialogOpen(true)
	}
	function closePcpDialog() {
		setPcpDialogOpen(false)
		setPcpSelected(null)
	}
	function handlePcpSearchChange(term: string) {
		setPcpSearchTerm(term)
		if (pcpDebounceRef.current) clearTimeout(pcpDebounceRef.current)
		pcpDebounceRef.current = setTimeout(() => setPcpDebouncedTerm(term), 300)
	}
	function confirmPcp() {
		if (!pcpSelected) return
		setPcp.mutate({ id: patientId, pcpId: pcpSelected.id } as any)
	}
	function confirmCreatePcp() {
		if (!pcpNewForm.companyName.trim()) return
		createPcpReferral.mutate({
			companyName: pcpNewForm.companyName.trim(),
			contactName: pcpNewForm.contactName.trim() || null,
			address: pcpNewForm.address.trim() || null,
			phoneNumber: pcpNewForm.phoneNumber.trim() || null,
			faxNumber: pcpNewForm.faxNumber.trim() || null,
			email: pcpNewForm.email.trim() || null,
			isPcp: true,
		} as any)
	}

	const diagnosisMap = Object.fromEntries((allDiagnoses ?? []).map((d) => [d.id, d]))
	const referralMap = Object.fromEntries((allReferrals ?? []).map((r) => [r.id, r]))

	function openAddDiagnosis() {
		setEditingDiagnosis(null)
		setDxDiagnosisId("")
		setDxType("secondary")
		setDxYear(String(new Date().getFullYear()))
		setDiagnosisDialogOpen(true)
	}
	function openEditDiagnosis(pd: PatientDiagnosis) {
		setEditingDiagnosis(pd)
		setDxDiagnosisId(pd.diagnosisId)
		setDxType((pd.type as "primary" | "secondary") ?? "secondary")
		setDxYear(pd.yearDiagnosed ? String(pd.yearDiagnosed) : "")
		setDiagnosisDialogOpen(true)
	}
	function closeDiagnosisDialog() {
		setDiagnosisDialogOpen(false)
		setEditingDiagnosis(null)
	}
	function doSaveDiagnosis() {
		const year = dxYear ? parseInt(dxYear, 10) : undefined
		if (editingDiagnosis) {
			updateDiagnosis.mutate({
				id: editingDiagnosis.id,
				diagnosisId: dxDiagnosisId,
				type: dxType,
				yearDiagnosed: year ?? null,
			})
		} else {
			createDiagnosis.mutate({
				patientId,
				diagnosisId: dxDiagnosisId,
				type: dxType,
				yearDiagnosed: year,
			})
		}
	}
	function saveDiagnosis() {
		if (!dxDiagnosisId) return
		if (dxType === "primary") {
			const existing = (patientDiagnoses ?? []).find(
				(pd) => pd.type === "primary" && pd.id !== editingDiagnosis?.id,
			)
			if (existing) {
				setPrimaryToDowngrade(existing)
				setPrimaryConflictOpen(true)
				return
			}
		}
		doSaveDiagnosis()
	}
	async function confirmChangePrimary() {
		if (!primaryToDowngrade) return
		setPrimaryConflictOpen(false)
		await downgradePrimary.mutateAsync({
			id: primaryToDowngrade.id,
			diagnosisId: primaryToDowngrade.diagnosisId,
			type: "secondary",
			yearDiagnosed: primaryToDowngrade.yearDiagnosed,
		})
		doSaveDiagnosis()
	}

	function openAddReferral() {
		setEditingReferral(null)
		setRefReferralId("")
		setRefDirection("outgoing")
		setRefDate("")
		setRefNotes("")
		setReferralDialogOpen(true)
	}
	function openEditReferral(pr: PatientReferral) {
		setEditingReferral(pr)
		setRefReferralId(pr.referralId)
		setRefDirection((pr.direction as "incoming" | "outgoing") ?? "outgoing")
		setRefDate(pr.referralDate ?? "")
		setRefNotes(pr.notes ?? "")
		setReferralDialogOpen(true)
	}
	function closeReferralDialog() {
		setReferralDialogOpen(false)
		setEditingReferral(null)
	}
	function saveReferral() {
		if (!refReferralId) return
		if (editingReferral) {
			updateReferral.mutate({
				id: editingReferral.id,
				referralId: refReferralId,
				direction: refDirection,
				referralDate: refDate || null,
				notes: refNotes || null,
			})
		} else {
			createReferral.mutate({
				patientId,
				referralId: refReferralId,
				direction: refDirection,
				referralDate: refDate || undefined,
				notes: refNotes || undefined,
			})
		}
	}

	const isLoading = patientLoading || addressesLoading
	const displayPatient = patient ?? (MOCK_PATIENT as any)

	const diagnosisSaving = createDiagnosis.isPending || updateDiagnosis.isPending
	const referralSaving = createReferral.isPending || updateReferral.isPending

	return (
		<Page centerVertical={false}>
			<PatientHeader patient={displayPatient} patientId={patientId} isDirty={false} />
			<Container width="xl" padding="lg" gap="lg">
				{isLoading ? (
					<Placeholder title="Loading..." />
				) : (
					<>
						{/* Personal information */}
						<Card>
							<CardHeader>
								<CardTitle>Personal Information</CardTitle>
							</CardHeader>
							<CardContent>
								<PatientForm patient={patient ?? undefined} />
							</CardContent>
						</Card>

						{/* Addresses */}
						<Card>
							<CardHeader>
								<div className="flex items-center justify-between">
									<CardTitle>Addresses</CardTitle>
									{!showAddAddress && (
										<Button
											size="sm"
											variant="outline"
											onClick={() => {
												setEditingAddressId(null)
												setShowAddAddress(true)
											}}
										>
											<Plus className="w-4 h-4 mr-1" />
											Add Address
										</Button>
									)}
								</div>
							</CardHeader>
							<CardContent className="flex flex-col gap-4">
								{showAddAddress && (
									<div className="border rounded-lg p-4">
										<p className="text-sm font-medium mb-3">New Address</p>
										<AddressForm
											patientId={patientId}
											onSuccess={() => {
												setShowAddAddress(false)
												utils.address.getAllByFilter.invalidate()
											}}
										/>
										<Button
											type="button"
											variant="ghost"
											size="sm"
											className="mt-2"
											onClick={() => setShowAddAddress(false)}
										>
											Cancel
										</Button>
									</div>
								)}
								{!addresses || addresses.length === 0 ? (
									!showAddAddress && (
										<p className="text-sm text-muted-foreground">No addresses on file.</p>
									)
								) : (
									addresses.map((address: Address) => (
										<AddressRow
											key={address.id}
											address={address}
											isEditing={editingAddressId === address.id}
											onEdit={() => {
												setShowAddAddress(false)
												setEditingAddressId(address.id)
											}}
											onEditDone={() => {
												setEditingAddressId(null)
												utils.address.getAllByFilter.invalidate()
											}}
											onDelete={() =>
												deleteAddress.mutate({ id: address.id, active: false })
											}
										/>
									))
								)}
							</CardContent>
						</Card>

						{/* Diagnoses */}
						<Card>
							<CardHeader>
								<div className="flex items-center justify-between">
									<CardTitle>Diagnoses</CardTitle>
									<Button size="sm" variant="outline" onClick={openAddDiagnosis}>
										<Plus className="w-4 h-4 mr-1" /> Add Diagnosis
									</Button>
								</div>
							</CardHeader>
							<CardContent>
								{!patientDiagnoses || patientDiagnoses.length === 0 ? (
									<p className="text-sm text-muted-foreground">No diagnoses on file.</p>
								) : (
									<div className="divide-y rounded-lg border">
										{patientDiagnoses.map((pd) => {
											const dx = diagnosisMap[pd.diagnosisId]
											return (
												<div key={pd.id} className="flex items-center gap-3 px-4 py-3">
													<div className="flex-1 min-w-0">
														<div className="flex items-center gap-2 flex-wrap">
															<span className="text-sm font-medium">{dx?.name ?? "Unknown"}</span>
															<Badge variant={pd.type === "primary" ? "default" : "secondary"} className="text-xs capitalize">
																{pd.type}
															</Badge>
															{pd.yearDiagnosed && (
																<span className="text-xs text-muted-foreground">Diagnosed {pd.yearDiagnosed}</span>
															)}
														</div>
													</div>
													<div className="flex items-center gap-1 shrink-0">
														<Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditDiagnosis(pd)}>
															<Pencil className="w-3.5 h-3.5" />
														</Button>
														<Button
															variant="ghost"
															size="icon"
															className="h-8 w-8 text-destructive hover:text-destructive"
															onClick={() => removeDiagnosis.mutate({ id: pd.id, active: false })}
														>
															<Trash2 className="w-3.5 h-3.5" />
														</Button>
													</div>
												</div>
											)
										})}
									</div>
								)}
							</CardContent>
						</Card>

						{/* Referrals */}
						<Card>
							<CardHeader>
								<div className="flex items-center justify-between">
									<CardTitle>Referrals</CardTitle>
									<Button size="sm" variant="outline" onClick={openAddReferral}>
										<Plus className="w-4 h-4 mr-1" /> Add Referral
									</Button>
								</div>
							</CardHeader>
							<CardContent>
								{!patientReferrals || patientReferrals.length === 0 ? (
									<p className="text-sm text-muted-foreground">No referrals on file.</p>
								) : (
									<div className="divide-y rounded-lg border">
										{patientReferrals.map((pr) => {
											const ref = referralMap[pr.referralId]
											return (
												<div key={pr.id} className="flex items-center gap-3 px-4 py-3">
													<div className="flex-1 min-w-0">
														<div className="flex items-center gap-2 flex-wrap">
															{pr.direction === "incoming" ? (
																<ArrowDownLeft className="w-4 h-4 text-blue-500 shrink-0" />
															) : (
																<ArrowUpRight className="w-4 h-4 text-green-500 shrink-0" />
															)}
															<span className="text-sm font-medium">{ref?.companyName ?? "Unknown"}</span>
															<Badge variant="outline" className="text-xs capitalize">
																{pr.direction}
															</Badge>
															{pr.referralDate && (
																<span className="text-xs text-muted-foreground">{pr.referralDate}</span>
															)}
														</div>
														{pr.notes && (
															<p className="text-xs text-muted-foreground mt-1 truncate">{pr.notes}</p>
														)}
													</div>
													<div className="flex items-center gap-1 shrink-0">
														{pr.direction === "outgoing" && (
															<Button
																variant="ghost"
																size="icon"
																className="h-8 w-8"
																title="Generate referral PDF"
																onClick={() => {
																	setReferralPdfTarget(pr)
																	setReferralPdfReason("")
																	setReferralPdfDialogOpen(true)
																}}
															>
																<FileDown className="w-3.5 h-3.5" />
															</Button>
														)}
														<Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditReferral(pr)}>
															<Pencil className="w-3.5 h-3.5" />
														</Button>
														<Button
															variant="ghost"
															size="icon"
															className="h-8 w-8 text-destructive hover:text-destructive"
															onClick={() => removeReferral.mutate({ id: pr.id, active: false })}
														>
															<Trash2 className="w-3.5 h-3.5" />
														</Button>
													</div>
												</div>
											)
										})}
									</div>
								)}
							</CardContent>
						</Card>

						{/* Primary Care Provider */}
						<Card>
							<CardHeader>
								<div className="flex items-center justify-between">
									<CardTitle className="flex items-center gap-2">
										<Stethoscope className="w-4 h-4" />
										Primary Care Provider
									</CardTitle>
									<Button size="sm" variant="outline" onClick={openPcpDialog}>
										<Pencil className="w-4 h-4 mr-1" />
										{patient?.pcpId ? "Change" : "Assign"}
									</Button>
								</div>
							</CardHeader>
							<CardContent>
								{patient?.pcpId && currentPcp ? (
									<div className="flex items-start justify-between gap-4">
										<div className="flex items-start gap-3">
											<UserCheck className="w-5 h-5 text-primary mt-0.5 shrink-0" />
											<div className="flex flex-col gap-0.5 text-sm">
												<span className="font-medium">{currentPcp.contactName || currentPcp.companyName}</span>
												{currentPcp.contactName && (
													<span className="text-muted-foreground">{currentPcp.companyName}</span>
												)}
												{currentPcp.address && (
													<span className="text-muted-foreground">{currentPcp.address}</span>
												)}
												{currentPcp.phoneNumber && (
													<span className="text-muted-foreground">Phone: {currentPcp.phoneNumber}</span>
												)}
												{currentPcp.faxNumber && (
													<span className="text-muted-foreground">Fax: {currentPcp.faxNumber}</span>
												)}
											</div>
										</div>
										<Button
											size="sm"
											variant="ghost"
											className="text-destructive hover:text-destructive shrink-0"
											onClick={() => removePcp.mutate({ id: patientId, pcpId: null } as any)}
											disabled={removePcp.isPending}
										>
											<X className="w-4 h-4 mr-1" />
											Remove
										</Button>
									</div>
								) : (
									<p className="text-sm text-muted-foreground">No primary care provider assigned.</p>
								)}
							</CardContent>
						</Card>

						{/* Forms */}
						<Card>
							<CardHeader className="flex flex-row items-center justify-between gap-2">
								<CardTitle className="flex items-center gap-2">
									<FileText className="w-4 h-4" />
									Forms
								</CardTitle>
								<Button
									size="sm"
									variant="outline"
									onClick={() => { setAssignFormId(""); setAssignFormDialogOpen(true) }}
								>
									<Plus className="w-4 h-4 mr-1" />
									Assign Form
								</Button>
							</CardHeader>
							<CardContent className="flex flex-col gap-2">
								{!patientForms || patientForms.length === 0 ? (
									<p className="text-sm text-muted-foreground">No forms assigned.</p>
								) : (
									patientForms.map((pf) => {
										const isCompleted = pf.status === "completed"
										const completedDate = pf.completedAt
											? new Date(pf.completedAt).toLocaleDateString("en-CA", { year: "numeric", month: "short", day: "numeric" })
											: null
										return (
											<div key={pf.id} className="flex items-center gap-3 rounded-md border px-3 py-2">
												<div className="shrink-0">
													{isCompleted
														? <CheckCircle className="w-4 h-4 text-green-600" />
														: <Clock className="w-4 h-4 text-amber-500" />
													}
												</div>
												<div className="flex-1 min-w-0">
													<p className="text-sm font-medium">{pf.formName}</p>
													{isCompleted && completedDate
														? <p className="text-xs text-muted-foreground">Completed {completedDate}</p>
														: <p className="text-xs text-muted-foreground capitalize">{pf.status.replace("_", " ")}</p>
													}
												</div>
												<Link to="/forms/$patientFormId" params={{ patientFormId: pf.id }}>
													<span className="text-xs text-teal-700 hover:underline font-medium">
														{isCompleted ? "Review" : pf.status === "in_progress" ? "Continue" : "Open"}
													</span>
												</Link>
											</div>
										)
									})
								)}
							</CardContent>
						</Card>
					</>
				)}
			</Container>

			{/* Diagnosis dialog */}
			<Dialog open={diagnosisDialogOpen} onOpenChange={(o) => { if (!o) closeDiagnosisDialog() }}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>{editingDiagnosis ? "Edit Diagnosis" : "Add Diagnosis"}</DialogTitle>
					</DialogHeader>
					<div className="flex flex-col gap-4 py-1">
						<div className="flex flex-col gap-1.5">
							<label className="text-sm font-medium">Diagnosis <span className="text-destructive">*</span></label>
							<Select value={dxDiagnosisId} onValueChange={setDxDiagnosisId}>
								<SelectTrigger>
									<SelectValue placeholder="Select diagnosis…" />
								</SelectTrigger>
								<SelectContent>
									{(allDiagnoses ?? []).map((d) => (
										<SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div className="flex flex-col gap-1.5">
							<label className="text-sm font-medium">Type</label>
							<Select value={dxType} onValueChange={(v) => setDxType(v as "primary" | "secondary")}>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{DIAGNOSIS_TYPES.map((t) => (
										<SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div className="flex flex-col gap-1.5">
							<label className="text-sm font-medium">Year Diagnosed</label>
							<Input
								type="number"
								placeholder="e.g. 2021"
								value={dxYear}
								onChange={(e) => setDxYear(e.target.value)}
								min={1900}
								max={new Date().getFullYear()}
							/>
						</div>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={closeDiagnosisDialog}>Cancel</Button>
						<Button onClick={saveDiagnosis} disabled={!dxDiagnosisId || diagnosisSaving}>
							{diagnosisSaving ? "Saving…" : "Save"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Primary diagnosis conflict confirmation */}
			<Dialog open={primaryConflictOpen} onOpenChange={(o) => { if (!o) setPrimaryConflictOpen(false) }}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>Change Primary Diagnosis?</DialogTitle>
					</DialogHeader>
					<div className="py-2 text-sm text-muted-foreground">
						<strong className="text-foreground">
							{diagnosisMap[primaryToDowngrade?.diagnosisId ?? ""]?.name ?? "Another diagnosis"}
						</strong>{" "}
						is currently the primary diagnosis. Setting this as primary will change it to secondary. Continue?
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => setPrimaryConflictOpen(false)}>Cancel</Button>
						<Button onClick={confirmChangePrimary} disabled={downgradePrimary.isPending}>
							{downgradePrimary.isPending ? "Saving…" : "Change Primary"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Referral dialog */}
			<Dialog open={referralDialogOpen} onOpenChange={(o) => { if (!o) closeReferralDialog() }}>
				<DialogContent className="sm:max-w-md">
					<DialogHeader>
						<DialogTitle>{editingReferral ? "Edit Referral" : "Add Referral"}</DialogTitle>
					</DialogHeader>
					<div className="flex flex-col gap-4 py-1">
						<div className="flex flex-col gap-1.5">
							<label className="text-sm font-medium">Referral Source <span className="text-destructive">*</span></label>
							<Select value={refReferralId} onValueChange={setRefReferralId}>
								<SelectTrigger>
									<SelectValue placeholder="Select referral…" />
								</SelectTrigger>
								<SelectContent>
									{(allReferrals ?? []).map((r) => (
										<SelectItem key={r.id} value={r.id}>{r.companyName}</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div className="flex flex-col gap-1.5">
							<label className="text-sm font-medium">Direction</label>
							<Select value={refDirection} onValueChange={(v) => setRefDirection(v as "incoming" | "outgoing")}>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{REFERRAL_DIRECTIONS.map((d) => (
										<SelectItem key={d} value={d} className="capitalize">{d}</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div className="flex flex-col gap-1.5">
							<label className="text-sm font-medium">Date of Referral</label>
							<Input
								type="date"
								value={refDate}
								onChange={(e) => setRefDate(e.target.value)}
							/>
						</div>
						<div className="flex flex-col gap-1.5">
							<label className="text-sm font-medium">Notes</label>
							<Textarea
								placeholder="Any notes about this referral…"
								value={refNotes}
								onChange={(e) => setRefNotes(e.target.value)}
								rows={3}
							/>
						</div>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={closeReferralDialog}>Cancel</Button>
						<Button onClick={saveReferral} disabled={!refReferralId || referralSaving}>
							{referralSaving ? "Saving…" : "Save"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Referral PDF dialog */}
			<Dialog open={referralPdfDialogOpen} onOpenChange={(o) => { if (!o) resetReferralPdfDialog() }}>
				<DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
					<DialogHeader>
						<DialogTitle>Generate Referral PDF</DialogTitle>
					</DialogHeader>
					<div className="flex flex-col gap-4 py-1">
						{referralPdfTarget && (
							<p className="text-sm text-muted-foreground">
								Generating a referral letter to{" "}
								<span className="font-medium text-foreground">
									{referralMap[referralPdfTarget.referralId]?.companyName ?? "the referral"}
								</span>.
							</p>
						)}

						{/* Referring Practitioner */}
						<div className="flex flex-col gap-1.5">
							<label className="text-sm font-medium">
								Referring Practitioner <span className="text-muted-foreground font-normal">(optional)</span>
							</label>
							<Select value={referralPdfPractitionerId} onValueChange={setReferralPdfPractitionerId}>
								<SelectTrigger>
									<SelectValue placeholder="Select practitioner…" />
								</SelectTrigger>
								<SelectContent>
									{(allPractitioners ?? []).map((p: any) => (
										<SelectItem key={p.id} value={p.id}>
											{[p.firstName, p.lastName].filter(Boolean).join(" ")}
											{p.professionalDesignation ? `, ${p.professionalDesignation}` : ""}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						{/* Referral Priority */}
						<div className="flex flex-col gap-2">
							<label className="text-sm font-medium">Referral Priority</label>
							<div className="flex flex-wrap gap-x-5 gap-y-2">
								{(["routine", "semi_urgent", "urgent", "other"] as const).map((p) => (
									<label key={p} className="flex items-center gap-2 cursor-pointer text-sm">
										<input
											type="radio"
											name="referralPriority"
											value={p}
											checked={referralPdfPriority === p}
											onChange={() => setReferralPdfPriority(p)}
											className="accent-teal-700"
										/>
										{p === "routine" ? "Routine" : p === "semi_urgent" ? "Semi-Urgent" : p === "urgent" ? "Urgent" : "Other"}
									</label>
								))}
							</div>
							{referralPdfPriority === "other" && (
								<Input
									placeholder="Specify priority…"
									value={referralPdfPriorityOther}
									onChange={(e) => setReferralPdfPriorityOther(e.target.value)}
								/>
							)}
						</div>

						{/* Type of Referral */}
						<div className="flex flex-col gap-2">
							<label className="text-sm font-medium">Type of Referral</label>
							<div className="grid grid-cols-2 gap-x-5 gap-y-2">
								{([
									{ key: "consultation",       label: "Consultation"       },
									{ key: "assessment",         label: "Assessment"         },
									{ key: "diagnostic_testing", label: "Diagnostic Testing" },
									{ key: "treatment",          label: "Treatment"          },
									{ key: "follow_up",          label: "Follow-up"          },
									{ key: "other",              label: "Other"              },
								] as const).map(({ key, label }) => (
									<label key={key} className="flex items-center gap-2 cursor-pointer text-sm">
										<Checkbox
											checked={referralPdfTypes.includes(key)}
											onCheckedChange={(checked) =>
												setReferralPdfTypes((prev) =>
													checked ? [...prev, key] : prev.filter((t) => t !== key),
												)
											}
										/>
										{label}
									</label>
								))}
							</div>
							{referralPdfTypes.includes("other") && (
								<Input
									placeholder="Specify type…"
									value={referralPdfTypeOther}
									onChange={(e) => setReferralPdfTypeOther(e.target.value)}
								/>
							)}
						</div>

						{/* Reason for Referral */}
						<div className="flex flex-col gap-1.5">
							<label className="text-sm font-medium">
								Reason for Referral / Clinical Question <span className="text-muted-foreground font-normal">(optional)</span>
							</label>
							<p className="text-xs text-muted-foreground -mt-0.5">Please assess/evaluate the patient for:</p>
							<Textarea
								placeholder="e.g. obstructive sleep apnea, chronic pain management…"
								value={referralPdfReason}
								onChange={(e) => setReferralPdfReason(e.target.value)}
								rows={3}
							/>
						</div>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={resetReferralPdfDialog}>Cancel</Button>
						<Button
							disabled={!referralPdfTarget || generateReferralPdf.isPending}
							onClick={() => {
								if (!referralPdfTarget) return
								generateReferralPdf.mutate({
									patientReferralId:  referralPdfTarget.id,
									practitionerId:     referralPdfPractitionerId || null,
									priority:           referralPdfPriority || null,
									priorityOther:      referralPdfPriorityOther || null,
									referralTypes:      referralPdfTypes.length ? referralPdfTypes : null,
									referralTypeOther:  referralPdfTypeOther || null,
									reason:             referralPdfReason || null,
								})
							}}
						>
							{generateReferralPdf.isPending ? (
								<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating…</>
							) : (
								<><FileDown className="w-4 h-4 mr-2" />Generate PDF</>
							)}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* PCP dialog */}
			<Dialog open={pcpDialogOpen} onOpenChange={(o) => { if (!o) closePcpDialog() }}>
				<DialogContent className="sm:max-w-lg">
					<DialogHeader>
						<DialogTitle>Assign Primary Care Provider</DialogTitle>
					</DialogHeader>

					{pcpMode === "search" ? (
						<div className="flex flex-col gap-4 py-1">
							<div className="flex flex-col gap-1.5">
								<label className="text-sm font-medium">Search by name, address, or phone</label>
								<div className="relative">
									<Input
										placeholder="e.g. Dr. Smith, 613-555…"
										value={pcpSearchTerm}
										onChange={(e) => handlePcpSearchChange(e.target.value)}
										autoFocus
									/>
									{pcpSearchLoading && (
										<Loader2 className="w-4 h-4 animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
									)}
								</div>
							</div>

							{pcpDebouncedTerm.length >= 2 && (
								<div className="rounded-lg border divide-y max-h-64 overflow-y-auto">
									{!pcpSearchResults || pcpSearchResults.length === 0 ? (
										<p className="px-4 py-3 text-sm text-muted-foreground">No results found.</p>
									) : (
										pcpSearchResults.map((r) => (
											<button
												key={r.id}
												type="button"
												className={`w-full text-left px-4 py-3 hover:bg-muted/50 transition-colors ${pcpSelected?.id === r.id ? "bg-primary/10 border-l-2 border-primary" : ""}`}
												onClick={() => setPcpSelected(r)}
											>
												<p className="text-sm font-medium">{r.contactName || r.companyName}</p>
												{r.contactName && <p className="text-xs text-muted-foreground">{r.companyName}</p>}
												{r.address && <p className="text-xs text-muted-foreground">{r.address}</p>}
												{r.phoneNumber && <p className="text-xs text-muted-foreground">Phone: {r.phoneNumber}</p>}
											</button>
										))
									)}
								</div>
							)}

							{pcpSelected && (
								<div className="rounded-lg border border-primary/30 bg-primary/5 p-4">
									<div className="flex items-start justify-between gap-2">
										<div className="flex flex-col gap-0.5 text-sm">
											<span className="font-semibold">{pcpSelected.contactName || pcpSelected.companyName}</span>
											{pcpSelected.contactName && <span className="text-muted-foreground">{pcpSelected.companyName}</span>}
											{pcpSelected.address && <span className="text-muted-foreground">{pcpSelected.address}</span>}
											{pcpSelected.phoneNumber && <span className="text-muted-foreground">Phone: {pcpSelected.phoneNumber}</span>}
											{pcpSelected.faxNumber && <span className="text-muted-foreground">Fax: {pcpSelected.faxNumber}</span>}
											{pcpSelected.email && <span className="text-muted-foreground">{pcpSelected.email}</span>}
										</div>
										<button type="button" onClick={() => setPcpSelected(null)} className="text-muted-foreground hover:text-foreground shrink-0">
											<X className="w-4 h-4" />
										</button>
									</div>
								</div>
							)}

							<button
								type="button"
								className="text-sm text-primary hover:underline text-left"
								onClick={() => setPcpMode("create")}
							>
								+ Can't find it? Create a new practitioner record
							</button>
						</div>
					) : (
						<div className="flex flex-col gap-3 py-1">
							<p className="text-sm text-muted-foreground">Fill in the new practitioner's information.</p>
							{[
								{ key: "companyName" as const, label: "Clinic / Organization", placeholder: "e.g. Ottawa Family Health" },
								{ key: "contactName" as const, label: "Doctor Full Name", required: true, placeholder: "e.g. Dr. Jane Smith" },
								{ key: "address" as const, label: "Full Address", placeholder: "123 Main St, Ottawa, ON" },
								{ key: "phoneNumber" as const, label: "Phone", required: true, placeholder: "613-555-0100" },
								{ key: "faxNumber" as const, label: "Fax", required: true, placeholder: "613-555-0101" },
								{ key: "email" as const, label: "Email", placeholder: "clinic@example.com" },
							].map(({ key, label, required, placeholder }) => (
								<div key={key} className="flex flex-col gap-1.5">
									<label className="text-sm font-medium">
										{label} {required && <span className="text-destructive">*</span>}
									</label>
									<Input
										placeholder={placeholder}
										value={pcpNewForm[key]}
										onChange={(e) => setPcpNewForm((s) => ({ ...s, [key]: e.target.value }))}
									/>
								</div>
							))}
							<div className="flex items-center gap-2 pt-1">
								<Checkbox id="newIsPcp" checked={pcpNewForm.isPcp} onCheckedChange={(c) => setPcpNewForm((s) => ({ ...s, isPcp: !!c }))} />
								<Label htmlFor="newIsPcp" className="cursor-pointer text-sm">Mark as Primary Care Provider</Label>
							</div>
							<button
								type="button"
								className="text-sm text-muted-foreground hover:underline text-left"
								onClick={() => setPcpMode("search")}
							>
								← Back to search
							</button>
						</div>
					)}

					<DialogFooter>
						<Button variant="outline" onClick={closePcpDialog}>Cancel</Button>
						{pcpMode === "search" ? (
							<Button
								onClick={confirmPcp}
								disabled={!pcpSelected || setPcp.isPending}
							>
								{setPcp.isPending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving��</> : "Confirm Selection"}
							</Button>
						) : (
							<Button
								onClick={confirmCreatePcp}
								disabled={!pcpNewForm.contactName.trim() || !pcpNewForm.phoneNumber.trim() || !pcpNewForm.faxNumber.trim() || createPcpReferral.isPending || setPcp.isPending}
							>
								{createPcpReferral.isPending || setPcp.isPending
									? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving…</>
									: "Create & Assign"}
							</Button>
						)}
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Assign Form dialog */}
			<Dialog open={assignFormDialogOpen} onOpenChange={(o) => { if (!o) { setAssignFormDialogOpen(false); setAssignFormId("") } }}>
				<DialogContent className="sm:max-w-sm">
					<DialogHeader>
						<DialogTitle>Assign Form</DialogTitle>
					</DialogHeader>
					<div className="flex flex-col gap-3 py-1">
						<Label>Form</Label>
						<Select value={assignFormId} onValueChange={setAssignFormId}>
							<SelectTrigger>
								<SelectValue placeholder="Select a form…" />
							</SelectTrigger>
							<SelectContent>
								{(availableForms ?? []).map((f) => (
									<SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					<DialogFooter>
						<Button variant="outline" onClick={() => { setAssignFormDialogOpen(false); setAssignFormId("") }}>
							Cancel
						</Button>
						<Button
							disabled={!assignFormId || assignForm.isPending}
							onClick={() => assignForm.mutate({ patientId, formId: assignFormId } as any)}
						>
							{assignForm.isPending ? "Assigning…" : "Assign"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</Page>
	)
}

/* ─── Address row ────────────────────────────────────────────────────────── */

function AddressRow({
	address,
	isEditing,
	onEdit,
	onEditDone,
	onDelete,
}: {
	address: Address
	isEditing: boolean
	onEdit: () => void
	onEditDone: () => void
	onDelete: () => void
}) {
	const label = ADDRESS_TYPE_LABELS[address.type] ?? address.type

	if (isEditing) {
		return (
			<div className="border rounded-lg p-4">
				<p className="text-sm font-medium mb-3">{label}</p>
				<AddressForm entity={address} onSuccess={onEditDone} />
				<Button type="button" variant="ghost" size="sm" className="mt-2" onClick={onEditDone}>
					Cancel
				</Button>
			</div>
		)
	}

	return (
		<div className="border rounded-lg p-4 flex items-start justify-between gap-4">
			<div className="flex flex-col gap-1 text-sm">
				<div className="flex items-center gap-2">
					<Badge variant="secondary" className="text-xs capitalize">{label}</Badge>
				</div>
				<p className="mt-1">{address.line1}</p>
				{address.line2 && <p>{address.line2}</p>}
				<p>
					{address.city}
					{address.state ? `, ${address.state}` : ""}{" "}
					{address.postalCode}
				</p>
				<p className="text-muted-foreground">{address.country}</p>
			</div>
			<div className="flex gap-2 shrink-0">
				<Button variant="outline" size="sm" onClick={onEdit}>Edit</Button>
				<Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={onDelete}>
					Remove
				</Button>
			</div>
		</div>
	)
}
